import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useLocation } from 'react-router-dom';
import { Spin } from 'antd';

import { DEFAULT_DATE_FORMAT } from '@/contants/common';
import { formatDate } from '@/utils/date';

import { routeFileSelector } from '../../../../../utilz/i18n';
import FormCheckBox from '../../../../components/Inputs/FormCheckBox';
import BatchLabel from '../../../../components/Labels/BatchLabel';
import PrimaryLabel from '../../../../components/Labels/PrimaryLabel';
import SecondaryLabel from '../../../../components/Labels/SecondaryLabel';
import ScrollPagination from '../../../../components/Paginations/ScrollPagination';
import Paper from '../../../../components/Paper/Paper';
import ContentSpinner from '../../../../components/Spinners/ContentSpinner';
import { useGetTableViewColumns } from '../hooks';

const CHECKBOX_COLUMN_WIDTH = 32;

const LDTableView = ({
  handleLoadMore,
  isLoading,
  hasMore,
  claimsList,
  handleShipmentClick,
  onSelect,
  selectedClaims,
  isFirstTime,
}) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const { data: tableViewColumns = [], isFetching: isTableColumnsLoading } = useGetTableViewColumns();

  const handleCheckBoxClick = (e, data, isSelected) => {
    e.stopPropagation();
    onSelect(data.claimKey, isSelected);
  };

  const colSpan = useMemo(
    // biome-ignore lint/style/noMagicNumbers: TODO: investigate
    () => (tableViewColumns.length ? tableViewColumns.length : 0) + 3,
    [tableViewColumns.length],
  );

  return isFirstTime ? (
    <Paper className="h-100 w-100 mb-15" />
  ) : (
    <div className="h-100 overflow-hidden pb-15">
      {isTableColumnsLoading ? (
        <div className="h-100 background-white br-4">
          <ContentSpinner size="large" />
        </div>
      ) : (
        <ScrollPagination
          className="background-white br-4 w-100 h-100 scrollable-table-container horizontal"
          loadMore={handleLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          scrollOffset={50}
          hideSpinner
        >
          <table className="w-100 background-white br-4">
            <thead>
              <tr>
                <th
                  className="background-header text-white fixed fixed-top fixed-left shadow-none"
                  style={{
                    maxWidth: CHECKBOX_COLUMN_WIDTH,
                    minWidth: CHECKBOX_COLUMN_WIDTH,
                    width: CHECKBOX_COLUMN_WIDTH,
                  }}
                />
                <th
                  className="background-header text-white fixed fixed-top fixed-left"
                  style={{
                    left: CHECKBOX_COLUMN_WIDTH,
                    whiteSpace: 'pre-wrap !important',
                  }}
                >
                  Claim Number
                </th>
                {tableViewColumns.map(({ columnName, columnValue }) => (
                  <th key={columnValue} className="background-header text-white fixed fixed-top">
                    {columnName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {claimsList.map((row) => (
                <tr key={row.claimKey}>
                  <td
                    className="fixed fixed-left shadow-none"
                    style={{
                      maxWidth: CHECKBOX_COLUMN_WIDTH,
                      minWidth: CHECKBOX_COLUMN_WIDTH,
                      width: CHECKBOX_COLUMN_WIDTH,
                    }}
                  >
                    <FormCheckBox
                      className="m-0"
                      checkBoxClass="normal-checkbox"
                      checked={selectedClaims.includes(row.claimKey)}
                      onChange={(e) => handleCheckBoxClick(e, row, selectedClaims.includes(row.claimKey))}
                    />
                  </td>
                  <td className="fixed fixed-left" style={{ left: CHECKBOX_COLUMN_WIDTH }}>
                    <span
                      className="cursor-pointer f-18 fw-700 text-header"
                      onClick={() => handleShipmentClick(row.claimKey)}
                      onKeyDown={() => handleShipmentClick(row.claimKey)}
                      role="button"
                      tabIndex={0}
                    >
                      {row.claimNumber}
                    </span>
                  </td>
                  {tableViewColumns.map(({ columnValue }) => (
                    <GenerateColumnValues key={columnValue} fieldName={columnValue} value={row[columnValue]} />
                  ))}
                </tr>
              ))}
              {isLoading ? (
                <tr>
                  <td colSpan={colSpan} className="background-white text-center">
                    <Spin spinning />
                  </td>
                </tr>
              ) : null}
              {!isLoading && claimsList?.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="background-white text-center">
                    <PrimaryLabel className="text-danger">
                      {t('translation:default.notification.emptyList')}.{'  '}
                      {t('translation:default.emptyRecords.title')}.
                    </PrimaryLabel>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </ScrollPagination>
      )}
    </div>
  );
};

const GenerateColumnValues = memo(({ fieldName, value }) => {
  const generateFieldValues = (fieldName, value) => {
    switch (fieldName) {
      case 'claimStatus':
        return (
          <td key={fieldName}>
            <BatchLabel>{value}</BatchLabel>
          </td>
        );

      case 'claimTotal':
        return (
          <td key={fieldName}>
            <PrimaryLabel className="text-iconDanger">
              <NumericFormat
                thousandsGroupStyle="thousand"
                displayType="text"
                value={Number(value).toFixed(2)}
                thousandSeparator
              />
            </PrimaryLabel>
          </td>
        );

      case 'claimPaidAmount':
        return (
          <td key={fieldName}>
            <PrimaryLabel className="text-success">
              <NumericFormat
                thousandsGroupStyle="thousand"
                displayType="text"
                value={Number(value).toFixed(2)}
                thousandSeparator
              />
            </PrimaryLabel>
          </td>
        );

      case 'difference':
        return (
          <td key={fieldName}>
            <PrimaryLabel className="text-header">
              <NumericFormat
                thousandsGroupStyle="thousand"
                displayType="text"
                value={Number(value).toFixed(2)}
                thousandSeparator
              />
            </PrimaryLabel>
          </td>
        );

      case 'dateFiled':
      case 'createdOn':
      case 'modifiedOn':
      case 'claimCloseDate':
        return (
          <td key={fieldName}>
            <SecondaryLabel className="f-15 fw-800 text-black">
              {value ? formatDate(value, DEFAULT_DATE_FORMAT) : 'NA'}
            </SecondaryLabel>
          </td>
        );

      default:
        return (
          <td key={fieldName}>
            <SecondaryLabel className="f-15 fw-600 text-black">{value}</SecondaryLabel>
          </td>
        );
    }
  };
  return <>{generateFieldValues(fieldName, value)}</>;
});

export default LDTableView;
